import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { MAIN_REGION_ID } from "./useRegionToggle";

const STORAGE_KEY = "sm.scrollByHistoryEntry";
const REMEMBERED_ENTRIES = 50;
const SAVE_THROTTLE_MS = 150;
const RESTORE_DEADLINE_MS = 2000;
const USER_SCROLL_INTENT = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

type SavedPositions = Record<string, number>;

const readPositions = (): SavedPositions => {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as SavedPositions) : {};
  } catch {
    return {};
  }
};

const savePosition = (entryKey: string, top: number) => {
  const positions = readPositions();
  delete positions[entryKey];
  positions[entryKey] = top;
  const kept = Object.entries(positions).slice(-REMEMBERED_ENTRIES);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    return;
  }
};

const scrollContainer = (): HTMLElement | null =>
  document.getElementById(MAIN_REGION_ID) ?? (document.scrollingElement as HTMLElement | null);

const canReach = (container: HTMLElement, top: number) => container.scrollHeight - container.clientHeight >= top;

export const useRestoreScrollOnBack = (): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entryKey = useRef(location.key);
  entryKey.current = location.key;
  const arrivedBy = useRef(navigationType);
  const restoring = useRef(false);

  useLayoutEffect(() => {
    const container = scrollContainer();
    if (!container) return;
    if (arrivedBy.current !== "POP") {
      container.scrollTop = 0;
      return;
    }
    const target = readPositions()[entryKey.current];
    if (target === undefined) return;

    restoring.current = true;
    const deadline = performance.now() + RESTORE_DEADLINE_MS;
    let frame: number | undefined;
    const stop = () => {
      restoring.current = false;
      if (frame !== undefined) cancelAnimationFrame(frame);
      USER_SCROLL_INTENT.forEach((type) => window.removeEventListener(type, stop, true));
    };
    USER_SCROLL_INTENT.forEach((type) => window.addEventListener(type, stop, true));

    const attempt = () => {
      if (canReach(container, target) || performance.now() >= deadline) {
        container.scrollTop = target;
        stop();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    frame = requestAnimationFrame(attempt);
    return stop;
  }, []);

  useEffect(() => {
    const container = scrollContainer();
    if (!container) return;
    const scrollTarget: HTMLElement | Window = container === document.scrollingElement ? window : container;
    let latest: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      timer = undefined;
      if (latest !== undefined) savePosition(entryKey.current, latest);
    };
    const onScroll = () => {
      if (restoring.current) return;
      latest = container.scrollTop;
      timer ??= setTimeout(flush, SAVE_THROTTLE_MS);
    };
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollTarget.removeEventListener("scroll", onScroll);
      if (timer !== undefined) clearTimeout(timer);
      flush();
    };
  }, []);
};

export const useRestoreScrollLeftOnBack = (scroller: HTMLElement | null): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entryKey = useRef(`${location.key}:left`);
  entryKey.current = `${location.key}:left`;
  const arrivedBy = useRef(navigationType);

  useEffect(() => {
    if (!scroller) return;
    const target = arrivedBy.current === "POP" ? readPositions()[entryKey.current] : undefined;
    if (target !== undefined) scroller.scrollLeft = target;
    let latest: number | undefined;
    const onScroll = () => {
      latest = scroller.scrollLeft;
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (latest !== undefined) savePosition(entryKey.current, latest);
    };
  }, [scroller]);
};
